import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { serverMessage } from '../PageState';

/**
 * A list the company page loads on demand, with its own loading and error state so a failed load
 * never reads as an empty list. Call reload() to fetch; responses to an older request are ignored.
 *
 * @param {Function} loader - async () => data
 */
export const useCompanyList = (loader, initial = []) => {
  const [state, setState] = useState({ data: initial, loading: false, error: null, loaded: false });
  const loaderRef = useRef(loader);
  const requestId = useRef(0);

  useEffect(() => {
    loaderRef.current = loader;
  }, [loader]);

  const reload = useCallback(async () => {
    const thisRequest = ++requestId.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await loaderRef.current();
      if (thisRequest === requestId.current) setState({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error(err);
      if (thisRequest === requestId.current) setState((prev) => ({ ...prev, loading: false, error: err }));
    }
  }, []);

  const setData = useCallback((update) => {
    setState((prev) => ({ ...prev, data: typeof update === 'function' ? update(prev.data) : update }));
  }, []);

  return { ...state, reload, setData };
};

const sameValues = (a, b) => Object.keys(a).every((key) => a[key] === b[key]);

/**
 * One settings form on the company page. It keeps its own draft of a few fields and saves only
 * those: onSaveCompany merges them into the last saved company, so one form never sends another
 * form's unsaved edits. Reports whether it has unsaved changes through onDirtyChange(formKey, dirty).
 *
 * @param {Function} toDraft - company => the form's fields, normalised for its inputs (module level, stable)
 * @param {Function} onSaveCompany - async (fields) => the saved company
 */
export const useCompanyForm = ({ formKey, company, toDraft, onSaveCompany, onDirtyChange }) => {
  const saved = useMemo(() => toDraft(company), [company, toDraft]);
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const dirty = !sameValues(draft, saved);

  useEffect(() => {
    onDirtyChange?.(formKey, dirty);
  }, [formKey, dirty, onDirtyChange]);

  // A form that goes away (another section opened) no longer holds unsaved changes
  useEffect(() => () => onDirtyChange?.(formKey, false), [formKey, onDirtyChange]);

  const setField = useCallback((field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setStatus(null);
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      setStatus(null);
      const result = await onSaveCompany(draft);
      setDraft(toDraft(result));
      setStatus({ type: 'success', message: 'Saved' });
      return result;
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: serverMessage(err, 'Could not save. Try again.') });
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { draft, setField, dirty, saving, status, setStatus, save };
};

/**
 * Warns before unsaved changes are lost: a browser prompt on reload or close, and a blocker for
 * in-app navigation, including switching section on the same page (a change of ?tab=).
 * Returns the router blocker; show a dialog while blocker.state === 'blocked'.
 */
export const useLeaveGuard = (dirty) => {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty
    && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Saved or discarded while the dialog was up: nothing left to protect
  useEffect(() => {
    if (blocker.state === 'blocked' && !dirty) blocker.reset();
  }, [blocker, dirty]);

  return blocker;
};
